<script setup lang="ts">
import AppIcon from '@/components/AppIcon.vue';
import { computed, nextTick, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import MarkdownRender from '@/components/Markdown.vue';
import { MD_CODE_LANGS, MD_CODE_PROPS, MD_MERMAID_PROPS } from '@/lib/markstream';
import ToolFold from '@/components/ToolFold.vue';
import {
  ToolViewparts, CitationProvider, Composer, Tooltip, escapeCurrencyDollars,
  DialogRoot, DialogPortal, DialogOverlay, DialogContent, DialogTitle,
} from '@hq/chat-ui';
import Avatar from '@/components/Avatar.vue';
import TargetMenu from '@/components/TargetMenu.vue';
import { useQuickAskStore } from '@/stores/quickAsk';
import { useLaunchStore, type LaunchAgent } from '@/stores/launch';
import { useToastStore } from '@/stores/toasts';
import { useFileDrop } from '@/composables/useFileDrop';
import { useI18n } from '@/lib/i18n';

const { t } = useI18n();
const store = useQuickAskStore();
const launch = useLaunchStore();
const router = useRouter();
const toasts = useToastStore();

const draft = ref('');
const inputEl = ref<{ focus: () => void; pickFiles: () => void } | null>(null);
const threadEl = ref<HTMLElement | null>(null);
const launchError = ref<string | null>(null);

// Composer target: the fast lane ('fast') or a specific agent id. The chips
// below the bar switch it; the SAME input drives both. Fast = inline answer
// (no VM); an agent = create + route to Studio on send (the heavy microVM
// path). Picking an agent is free - the boot only happens on send.
const target = ref<'fast' | string>('fast');

const open = computed(() => store.open);
const busy = computed(() => store.loading || launch.creating);
const canSend = computed(() => draft.value.trim().length > 0 && !busy.value);
// Whether the fast lane has anything to show in the thread area.
const hasThread = computed(() => store.messages.length > 0 || store.loading || !!store.error);

// ── Attachments ─────────────────────────────────────────────────────
// The Composer renders chips + handles paste + the picker; drag-drop is wired
// here. The sandbox-less fast lane can't read a document, so attaching one
// routes to an agent (and the fast chip locks while files are staged).
const files = ref<File[]>([]);
const defaultAgent = computed<LaunchAgent | null>(
  () => launch.enabled.find((a) => a.is_default) ?? launch.enabled[0] ?? null,
);
const fastDisabled = computed(() => files.value.length > 0);

function addAttachments(list: File[]): void {
  if (!list.length) return;
  files.value = [...files.value, ...list];
  if (target.value === 'fast') {
    const a = defaultAgent.value;
    if (a) {
      target.value = a.id;
      toasts.info(t('quickask.attach_needs_agent', { name: a.display_name }));
    }
  }
  void nextTick(() => inputEl.value?.focus());
}
const { dragOver, onDragEnter, onDragOver, onDragLeave, onDrop } = useFileDrop(addAttachments);
function attach(): void { inputEl.value?.pickFiles(); }

// Show a handful of agents as chips, default (Max) first; the rest spill to
// the full picker via the "more" chip.
const MAX_CHIPS = 4;
const agentChips = computed<LaunchAgent[]>(() => launch.enabled.slice(0, MAX_CHIPS));
const hasMore = computed(() => launch.enabled.length > MAX_CHIPS);
const selectedAgent = computed<LaunchAgent | null>(() =>
  target.value === 'fast'
    ? null
    : launch.enabled.find((a) => a.id === target.value) ?? null,
);
const placeholder = computed(() =>
  selectedAgent.value
    ? t('quickask.placeholder_agent', { name: selectedAgent.value.display_name })
    : t('quickask.placeholder_fast'),
);

// Focus the input when the panel opens; reset to the fast lane and (lazily)
// hydrate the agent list. The fast conversation itself persists in its store
// across opens; the target selection does not. On close, return focus to
// whatever opened the panel (the Cmd-K trigger / header button) so keyboard
// users aren't dumped on <body>.
let restoreFocusEl: HTMLElement | null = null;
watch(open, async (isOpen) => {
  if (isOpen) {
    restoreFocusEl = document.activeElement as HTMLElement | null;
    target.value = 'fast';
    files.value = [];
    launchError.value = null;
    void launch.loadAgents();
    await nextTick();
    inputEl.value?.focus();
    scrollThread();
  } else if (restoreFocusEl) {
    restoreFocusEl.focus();
    restoreFocusEl = null;
  }
});

watch(() => store.messages.length, () => { void nextTick(scrollThread); });

function scrollThread(): void {
  const el = threadEl.value;
  if (el) el.scrollTop = el.scrollHeight;
}

// Show the standalone "Thinking…" row only until the assistant bubble has
// something to show (a tool step or streamed text); after that the bubble
// itself carries the progress.
const pendingThinking = computed(() => {
  if (!store.loading) return false;
  const last = store.messages[store.messages.length - 1];
  return !last || last.role !== 'assistant' || (!last.text && !(last.tools && last.tools.length));
});

function pickTarget(next: 'fast' | string): void {
  if (next === 'fast' && fastDisabled.value) return;  // can't quick-ask with attachments
  target.value = next;
  launchError.value = null;
  void nextTick(() => inputEl.value?.focus());
}

async function submit(): Promise<void> {
  const q = draft.value;
  if (!q.trim() || busy.value) return;
  if (target.value === 'fast') {
    draft.value = '';
    // Keep the composer focused (the send button would otherwise steal it) so
    // the user can fire off the next message without re-tapping the input.
    void nextTick(() => inputEl.value?.focus());
    await store.send(q);
    return;
  }
  await launchAgent(target.value, q);
}

// Create a fresh agent conversation, hand off the typed message, and route to
// Studio - which auto-sends it on arrival so the user lands mid-turn. The VM
// cold-boot happens HERE, on explicit send, never on chip-select.
async function launchAgent(agentId: string, text: string): Promise<void> {
  launchError.value = null;
  try {
    const id = await launch.createConversation(agentId);
    launch.armPrompt(id, text, files.value);
    files.value = [];
    draft.value = '';
    store.closePanel();
    void router.push({ name: 'studio', params: { id } });
  } catch (e) {
    launchError.value = (e as { error?: string }).error ?? t('quickask.launch_error');
  }
}

// Overflow: jump to the full conversations list with the agent picker armed.
function openMore(): void {
  store.closePanel();
  void router.push({ name: 'studio-list', query: { new: '1' } });
}

// Reka's Dialog owns Escape-to-close and overlay-click - both surface as
// update:open(false). The Composer keeps Enter-to-send (@submit), so the
// dialog needs no key handling of its own.
function onOpenChange(next: boolean): void {
  if (!next) store.closePanel();
}

// The input is a contenteditable Composer, so take over Reka's auto-focus and
// land the caret in it explicitly. On close we restore focus ourselves (the
// panel has no DialogTrigger for Reka to return to - the open-watcher captured
// whatever opened it), so prevent Reka's close-auto-focus too.
function onContentOpenAutoFocus(e: Event): void {
  e.preventDefault();
  void nextTick(() => inputEl.value?.focus());
}
function onContentCloseAutoFocus(e: Event): void {
  e.preventDefault();
}

function newChat(): void {
  store.reset();
  draft.value = '';
  void nextTick(() => inputEl.value?.focus());
}

// Open the quick chat in Studio. It stays a fast ("ask") thread - the
// conversation is already a real mode='fast' row, so it opens as a quick
// chat with a "Convert to agent" action there. We deliberately do NOT
// promote here (that flipped it to a full agent thread, which surprised
// users who just wanted to keep chatting).
function continueInStudio(): void {
  const id = store.conversationId;
  if (!id) return;
  store.closePanel();
  void router.push({ name: 'studio', params: { id } });
}

</script>

<template>
  <DialogRoot :open="open" @update:open="onOpenChange">
    <DialogPortal>
      <DialogOverlay class="qa-overlay" />
      <DialogContent
        class="qa"
        :class="{ 'qa--drag': dragOver }"
        :aria-describedby="undefined"
        @open-auto-focus="onContentOpenAutoFocus"
        @close-auto-focus="onContentCloseAutoFocus"
        @dragenter="onDragEnter"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @drop="onDrop"
      >
        <!-- Header: title + conversation actions + close. (The composer now
             lives at the BOTTOM - standard chat order - so the thread reads
             top-to-bottom and the input sits above the mobile keyboard.) -->
        <header class="qa__head">
          <DialogTitle class="qa__title">
            <AppIcon name="sparkle" :size="16" />
            {{ t('quickask.quick_ask') }}
          </DialogTitle>
          <div class="qa__head-actions">
            <template v-if="target === 'fast' && store.conversationId">
              <Tooltip :label="t('quickask.new')">
                <button class="qa__head-btn" type="button" :aria-label="t('quickask.new')" @click="newChat">
                  <AppIcon name="plus" :size="16" />
                </button>
              </Tooltip>
              <Tooltip :label="t('quickask.open_in_studio')">
                <button class="qa__head-btn" type="button" :aria-label="t('quickask.open_in_studio')" @click="continueInStudio">
                  <AppIcon name="arrow-square-out" :size="16" />
                </button>
              </Tooltip>
            </template>
            <button class="qa__head-btn" type="button" :aria-label="t('common.close')" @click="store.closePanel()">
              <AppIcon name="x" :size="16" />
            </button>
          </div>
        </header>

        <!-- Body: the thread (scrolls, newest at the bottom) or the empty /
             launcher hint. Fills the space between header and composer. -->
        <div class="qa__body">
          <template v-if="target === 'fast'">
            <div v-if="hasThread" ref="threadEl" class="qa__thread">
              <div
                v-for="(m, i) in store.messages"
                :key="i"
                class="qa__msg"
                :class="`qa__msg--${m.role}`"
              >
                <span class="qa__role">{{ m.role === 'user' ? t('quickask.you') : 'Q' }}</span>
                <p v-if="m.role === 'user'" class="qa__text">{{ m.text }}</p>
                <template v-else>
                  <CitationProvider v-if="m.text" :tools="m.tools">
                    <MarkdownRender
                      class="qa__md"
                      custom-id="quickask-msg"
                      :content="escapeCurrencyDollars(m.text)"
                      :code-renderer="'shiki'"
                      :langs="MD_CODE_LANGS"
                      :code-block-props="MD_CODE_PROPS"
                      :mermaid-props="MD_MERMAID_PROPS"
                      :final="!m.streaming"
                      :typewriter="false"
                      mode="chat"
                      :smooth-streaming="m.streaming ? 'auto' : false"
                      :max-live-nodes="m.streaming ? 0 : 320"
                      :batch-rendering="true"
                    />
                  </CitationProvider>
                  <ToolViewparts v-if="m.tools && m.tools.length" :tools="m.tools" />
                  <ToolFold v-if="m.tools && m.tools.length" :tools="m.tools" />
                </template>
              </div>

              <div v-if="pendingThinking" class="qa__loading">
                <AppIcon name="circle-notch" class="qa__spin" :size="15" />
                <span>{{ t('quickask.thinking') }}</span>
              </div>

              <p v-if="store.error" class="qa__error">{{ store.error }}</p>
            </div>

            <div v-else class="qa__hint">
              {{ t('quickask.hint_fast') }}
            </div>
          </template>

          <!-- AGENT target: a launcher, not an inline chat. -->
          <div v-else class="qa__hint qa__hint--agent">
            <p v-if="launchError" class="qa__error">{{ launchError }}</p>
            <template v-else>
              {{ t('quickask.hint_agent_before') }} <kbd>Enter</kbd>
              {{ t('quickask.hint_agent_mid') }}
              <strong>{{ selectedAgent?.display_name }}</strong>{{ t('quickask.hint_agent_after') }}
            </template>
          </div>
        </div>

        <!-- Compose: target chips + the composer bar, pinned to the bottom. -->
        <div class="qa__compose">
          <div class="qa__targets" role="group" :aria-label="t('quickask.send_to')">
            <Tooltip :label="fastDisabled ? 'Remove the attachment to use quick chat' : undefined">
              <button
                type="button"
                class="qa__chip"
                :class="{ 'qa__chip--active': target === 'fast', 'qa__chip--off': fastDisabled }"
                :disabled="fastDisabled"
                @click="pickTarget('fast')"
              >
                <AppIcon name="lightning" :size="13" />
                {{ t('quickask.quick_ask') }}
              </button>
            </Tooltip>
            <button
              v-for="a in agentChips"
              :key="a.id"
              type="button"
              class="qa__chip"
              :class="{ 'qa__chip--active': target === a.id }"
              @click="pickTarget(a.id)"
            >
              <Avatar :name="a.display_name" :src="a.avatar_url" size="xs" class="qa__chip-av" />
              {{ a.display_name }}
            </button>
            <Tooltip v-if="hasMore" :label="t('quickask.all_agents')">
              <button
                type="button"
                class="qa__chip qa__chip--more"
                :aria-label="t('quickask.all_agents')"
                @click="openMore"
              >
                <AppIcon name="dots-three" :size="14" />
              </button>
            </Tooltip>
          </div>
          <!-- Phones: the pills above wrap and eat vertical space, so they
               collapse into this dropdown (hidden on desktop). -->
          <TargetMenu
            :target="target"
            :agents="launch.enabled"
            :fast-disabled="fastDisabled"
            :has-more="hasMore"
            @pick="pickTarget"
            @more="openMore"
          />

          <div class="qa__bar">
            <Avatar
              v-if="selectedAgent"
              :name="selectedAgent.display_name"
              :src="selectedAgent.avatar_url"
              size="sm"
            />
            <AppIcon name="sparkle" v-else class="qa__bar-icon" :size="18" />
            <Composer
              ref="inputEl"
              v-model="draft"
              v-model:files="files"
              class="qa__input"
              :placeholder="placeholder"
              @submit="submit"
            />
            <Tooltip label="Attach file">
              <button
                class="qa__attach"
                type="button"
                aria-label="Attach file"
                @click="attach"
              >
                <AppIcon name="paperclip" :size="16" />
              </button>
            </Tooltip>
            <button
              class="qa__send"
              type="button"
              :disabled="!canSend"
              :aria-label="t('quickask.send')"
              @click="submit"
            >
              <AppIcon name="arrow-up" :size="16" />
            </button>
          </div>
        </div>

        <!-- Drop overlay while a file is dragged over the panel. -->
        <div v-if="dragOver" class="qa__drop">
          <AppIcon name="paperclip" :size="18" />
          {{ t('quickask.drop_attach') }}
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<!-- Unscoped: Reka portals the Dialog to <body>, so a scoped data-v hash would
     never reach the overlay/content. Selectors are `qa`-prefixed to avoid
     collisions (same pattern as Dialog.vue / SidePanel.vue). -->
<style lang="scss">
@layer components {
  // DialogOverlay: full-screen scrim only. The panel positions itself (Reka
  // does not center content the way the old grid overlay did).
  .qa-overlay {
    position: fixed;
    inset: 0;
    z-index: 220;
    background: var(--scrim);
    backdrop-filter: blur(8px);
    &[data-state='open'] { animation: qa-overlay-in var(--dur-2) var(--ease-out); }
    &[data-state='closed'] { animation: qa-overlay-out var(--dur-1) var(--ease-out); }
  }

  // DialogContent. Desktop: a floating card near the top, centred. A flex
  // column - header (top), thread body (fills + scrolls), composer (bottom) -
  // the standard chat order, so the input is always at the bottom.
  .qa {
    position: fixed;
    top: 12vh;
    left: 50%;
    transform: translateX(-50%);
    z-index: 221;
    width: min(680px, 94vw);
    max-height: 72vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: var(--surface-panel);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-card);
    box-shadow: var(--shadow-overlay);
    &.qa--drag { border-color: var(--accent-default); }
    &[data-state='open'] { animation: qa-in var(--dur-2) var(--ease-out); }
    &[data-state='closed'] { animation: qa-out var(--dur-1) var(--ease-out); }
  }

  // Phones: a full-screen sheet sized to the VISUAL viewport (--vvh), so when
  // the keyboard opens the sheet shrinks and the bottom composer stays just
  // above it (iOS doesn't shrink dvh for the keyboard - see useVisualViewport).
  @media (max-width: 479.98px) {
    .qa {
      top: 0;
      left: 0;
      right: 0;
      transform: none;
      width: 100%;
      height: var(--vvh, 100dvh);
      max-height: none;
      border: 0;
      border-radius: 0;
    }
  }

  // Header: title + conversation actions + close.
  .qa__head {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-3) var(--space-3) var(--space-4);
    border-bottom: 1px solid var(--border-subtle);
  }
  .qa__title {
    flex: 1;
    min-width: 0;
    margin: 0;
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-sm);
    font-weight: 600;
    color: var(--ink-display);
    & > svg { color: var(--ink-muted); flex-shrink: 0; }
  }
  .qa__head-actions { display: flex; align-items: center; gap: var(--space-1); }
  .qa__head-btn {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    display: inline-grid;
    place-items: center;
    border: 0;
    border-radius: var(--radius-input);
    background: transparent;
    color: var(--ink-muted);
    cursor: pointer;
    transition: background var(--dur-1) var(--ease-out), color var(--dur-1) var(--ease-out);
    &:hover { background: var(--surface-sunken); color: var(--ink-primary); }
    &:focus-visible { outline: 2px solid var(--border-focus); outline-offset: 2px; }
  }

  // Body: holds the thread (which scrolls) or the hint. Fills the middle.
  .qa__body {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  // Compose: chips + composer bar, pinned at the bottom.
  .qa__compose {
    flex-shrink: 0;
    border-top: 1px solid var(--border-subtle);
  }

  // Command bar
  .qa__bar {
    display: flex;
    /* Bottom-align so the icon + attach/send stay beside the cursor line
       as the field grows down (see .qa__input scroll note below). */
    align-items: flex-end;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-3) var(--space-3) var(--space-4);
  }
  .qa__bar-icon { flex-shrink: 0; color: var(--ink-muted); }

  // Target chips (above the composer in the compose area).
  .qa__targets {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-3) var(--space-3) 0;
  }
  .qa__chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: var(--radius-pill);
    border: 1px solid var(--border-subtle);
    background: var(--surface-panel);
    color: var(--ink-muted);
    font-size: var(--text-xs);
    font-weight: 500;
    cursor: pointer;
    transition: background var(--dur-1) var(--ease-out),
                color var(--dur-1) var(--ease-out),
                border-color var(--dur-1) var(--ease-out);

    & > svg { color: var(--ink-subtle); flex-shrink: 0; }
    &:hover:not(.qa__chip--active) { background: var(--surface-sunken); color: var(--ink-primary); }
    &--active {
      background: var(--accent-default);
      color: var(--accent-text);
      border-color: var(--accent-default);
      & > svg { color: var(--accent-text); }
    }
    &--more { padding: 5px 8px; }
    &:disabled, &--off { opacity: 0.4; cursor: default; }
    &:disabled:hover, &--off:hover {
      background: var(--surface-panel); color: var(--ink-muted); border-color: var(--border-subtle);
    }
  }
  // Avatar handles the circle + size; only the nudge into the chip padding is local.
  .qa__chip-av { margin-left: -2px; }
  /* The composer root does NOT scroll - the inner editor is the single scroll
     container (below). min-width/min-height:0 just let the flex item shrink. */
  .qa__input {
    flex: 1;
    min-width: 0;
    min-height: 0;
    border: 0;
    background: transparent;
    color: var(--ink-display);
    font: inherit;
    font-size: var(--text-md);
    line-height: 1.5;
    resize: none;
    outline: none;
  }
  /* The inner editor's cap + scroll lives UNLAYERED below the @layer block -
     it has to beat the shared Composer's unlayered 40vh rule, which a layered
     rule never can. */
  .qa__send,
  .qa__attach {
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    display: inline-grid;
    place-items: center;
    border: 0;
    border-radius: var(--radius-input);
    cursor: pointer;
    transition: background var(--dur-1) var(--ease-out),
                color var(--dur-1) var(--ease-out);
  }
  .qa__send {
    background: var(--accent-default);
    color: var(--accent-text);
    &:hover:not(:disabled) { background: var(--accent-hover); }
    &:disabled { opacity: 0.4; cursor: default; }
  }
  .qa__attach {
    background: transparent;
    color: var(--ink-muted);
    &:hover { background: var(--surface-sunken); color: var(--ink-primary); }
  }
  /* Drop overlay: covers the whole panel while a file is dragged in. */
  .qa__drop {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: var(--space-2);
    background: color-mix(in oklab, var(--surface-panel) 82%, transparent);
    backdrop-filter: blur(2px);
    border: 1.5px dashed var(--accent-default);
    border-radius: var(--radius-card);
    color: var(--ink-display);
    font-size: var(--text-sm);
    font-weight: 600;
    pointer-events: none;
    z-index: 3;
  }

  // Thread
  .qa__thread {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: var(--space-4);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }
  .qa__msg {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }
  .qa__role {
    font-size: var(--text-xs);
    font-weight: 600;
    letter-spacing: var(--tracking-wide);
    text-transform: uppercase;
    color: var(--ink-subtle);
  }
  .qa__msg--user .qa__role { color: var(--ink-muted); }
  .qa__text {
    margin: 0;
    font-size: var(--text-sm);
    line-height: 1.6;
    color: var(--ink-primary);
    white-space: pre-wrap;
    word-break: break-word;
  }
  .qa__msg--user .qa__text { color: var(--ink-display); font-weight: 500; }

  // Assistant answers render markdown via markstream-vue (headings, tables,
  // links, code). markstream ships its own scoped typography; we only match
  // the popover font size and keep wide content (tables/code) from blowing
  // out the narrow panel.
  .qa__md {
    min-width: 0;
    width: 100%;
    font-size: var(--text-sm);
    line-height: 1.6;
    color: var(--ink-primary);
    word-wrap: break-word;
  }
  .qa__md > *:first-child { margin-top: 0; }
  .qa__md > *:last-child { margin-bottom: 0; }
  .qa__md table {
    display: block;
    width: 100%;
    overflow-x: auto;
    border-collapse: collapse;
  }
  .qa__md pre { max-width: 100%; overflow-x: auto; }
  .qa__md img { max-width: 100%; height: auto; }

  .qa__loading {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-sm);
    color: var(--ink-muted);
  }
  .qa__spin { animation: qa-spin 0.7s linear infinite; }
  @keyframes qa-spin { to { transform: rotate(360deg); } }

  .qa__error {
    margin: 0;
    padding: var(--space-2) var(--space-3);
    font-size: var(--text-sm);
    color: var(--status-danger-text);
    background: var(--status-danger-soft);
    border-radius: var(--radius-input);
  }

  .qa__hint {
    padding: var(--space-4) var(--space-4) var(--space-5);
    font-size: var(--text-sm);
    line-height: 1.6;
    color: var(--ink-subtle);
  }
  .qa__hint--agent {
    & strong { color: var(--ink-primary); font-weight: 600; }
    & kbd {
      font-family: var(--font-mono);
      font-size: var(--text-xs);
      padding: 1px 5px;
      border-radius: var(--radius-sm);
      border: 1px solid var(--border-subtle);
      background: var(--surface-sunken);
      color: var(--ink-muted);
    }
  }

  // Phones: the sheet spans the safe areas (viewport-fit=cover). Pad the
  // header below the notch and the composer above the home indicator. The
  // inset-bottom collapses to 0 while the keyboard is up (home indicator
  // hidden), so the composer sits flush above the keyboard then.
  @media (max-width: 479.98px) {
    .qa__head { padding-top: calc(var(--space-3) + env(safe-area-inset-top, 0px)); }
    .qa__targets { display: none; }   // replaced by the TargetMenu dropdown
    .qa__bar {
      padding-bottom: calc(var(--space-3) + env(safe-area-inset-bottom, 0px));
      padding-left: calc(var(--space-4) + env(safe-area-inset-left, 0px));
      padding-right: calc(var(--space-3) + env(safe-area-inset-right, 0px));
    }
  }
}

/* UNLAYERED on purpose. The shared Composer caps its editor at 40vh + scroll
   with an UNLAYERED rule; per the cascade-layer spec unlayered always beats
   layered, so the @layer rules above (like every other view) can never win this
   one. Capping at ~4 lines (6.5em) + scroll keeps the field from growing to 40vh
   and being clipped by the modal's max-height. (0,4,0) out-specifies the base
   (0,3,0); not `scoped`, so the plain descendant chain reaches the deep editor. */
.qa .qa__input .hq-composer__surface .hq-composer__editor {
  max-height: 6.5em;
  overflow-y: auto;
}

// Open / close: fade the scrim, lift the panel. Reka drives the data-state
// attribute and keeps the element mounted through the close animation.
@keyframes qa-overlay-in { from { opacity: 0; } }
@keyframes qa-overlay-out { to { opacity: 0; } }
@keyframes qa-in {
  from { opacity: 0; transform: translate(-50%, -8px) scale(0.99); }
}
@keyframes qa-out {
  to { opacity: 0; transform: translate(-50%, -8px) scale(0.99); }
}
</style>
